import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { InnrHead } from 'containers'
import { makeStyles } from '@material-ui/core/styles'
import { Paper, Button, Box, Hidden, MenuItem } from '@material-ui/core'
import { DataGrid } from '@mui/x-data-grid'
import { players as playersModule } from 'redux/modules'
import { SelectedMenu } from 'components'
import useTeamFeature from 'hooks/useTeamFeature'
import PlayerDialog from 'containers/AddNewPlayer/components/PlayerDialog'
import { createTeamPlayerInOrganization } from 'containers/AddNewPlayer/api'
import AddPlayersDialog from '../AddPlayersDialog'
import AddOrganizationTeamPlayersDialog from '../AddOrganizationTeamPlayersDialog'
import columns from './GridColumns'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'
import {
  PLAYER_STATUS_ACTIVE,
  PLAYER_STATUS_DEACTIVATED,
} from 'constants/player'

const useStyles = makeStyles(() => ({
  dataGridTable: {
    borderLeft: '0 !important',
    borderRight: '0 !important',
    borderBottom: '0 !important',
    '& .MuiDataGrid-cell': {
      borderBottom: '0 !important',
      textTransform: 'capitalize',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: '0 !important',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none !important ',
    },
  },
}))

const TeamPlayersGrid = ({
  currentClubUuid,
  players,
  createTeamPlayer,
  updatePlayer,
  deletePlayer,
  team,
  fetchTeamPlayers,
}) => {
  const classes = useStyles()

  const [addPlayerIsOpen, setAddPlayerIsOpen] = useState(false)
  const [addPlayerIsLoading, setAddPlayerIsLoading] = useState(false)
  const [sortModel, handleSortModel] = useState([
    {
      field: 'firstName',
      sort: 'asc',
    },
  ])
  const [statusFilter, setStatusFilter] = useState(PLAYER_STATUS_ACTIVE)
  const [isOrgPlayerDialogOpen, setIsOrgPlayerDialogOpen] = useState(false)
  const [playerRow, setPlayerRow] = useState(players)
  const [errorMessage, setErrorMessage] = useState(null)

  const { enabled: accessClubPersons } = useTeamFeature(
    TEAM_FEATURE_FLAGS.access_club_persons
  )

  useEffect(() => {
    setPlayerRow(players)
  }, [players])

  const rows = useMemo(() => {
    return playerRow
      .filter((player) => {
        if (statusFilter === PLAYER_STATUS_ACTIVE) {
          return ['ACTIVE', 'IN_PROGRESS'].includes(player.status)
        }

        return ['DELETED', 'DEACTIVATED', 'TRANSFERRED'].includes(player.status)
      })
      .map((player) => ({
        id: player.uuid,
        uuid: player.uuid,
        avatar: player.relationships?.avatar?.full_url || null,
        fullName: `${player.first_name} ${player.last_name}`,
        number: player.number ? player.number : '',
        position: player.position ? player.position : '',
        status: player.status.toLowerCase(),
        onUpdate: (uuid, data) => {
          return updatePlayer(uuid, data).then((result) =>
            fetchTeamPlayers(team.uuid, {
              include: 'avatar',
              status: statusFilter,
            }).then(() => result)
          )
        },
        onDelete: (uuid) => {
          return deletePlayer(uuid)
        },
        player, // needed because of most crazy EditDialog implementation I can image :/
        showTransferOption:
          currentClubUuid !== process.env.REACT_APP_DEFAULT_CLUB_UUID,
      }))
  }, [playerRow, statusFilter, fetchTeamPlayers, team.uuid, updatePlayer])

  const handleAddPlayersOpen = () => {
    if (accessClubPersons) {
      setIsOrgPlayerDialogOpen(true)
    } else {
      setAddPlayerIsLoading(false)
      setAddPlayerIsOpen(true)
    }
  }

  const handleAddPlayersClose = () => {
    setAddPlayerIsOpen(false)
  }

  const handleAddPlayersSubmit = (data) => {
    setAddPlayerIsLoading(false)

    createTeamPlayer(team.uuid, { ...data, number: +data.number })
      .then(() => {
        setAddPlayerIsLoading(false)
      })
      .catch((error) => {
        console.log(error)
        setAddPlayerIsLoading(false)
      })
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
  }

  const handleOrgDialogClose = () => {
    setIsOrgPlayerDialogOpen(false)
  }

  const [selectedMember, setSelectedMember] = useState(null)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    position: '',
    number: '',
  })
  const [isNewPlayer, setIsNewPlayer] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSearchValue = (value) => {
    setSearchValue(value)
  }

  const handleAddClick = (member) => {
    setIsOrgPlayerDialogOpen(false)
    setOpenDialog(true)
    setErrorMessage(null)

    if (member) {
      setSelectedMember(member)
      setIsNewPlayer(false)
      setFormData({
        firstName: member.first_name || '',
        lastName: member.last_name || '',
        position: member.position || '',
        number: member.number || '',
      })
    } else {
      setSelectedMember(null)
      setIsNewPlayer(true)
      setFormData({
        firstName: searchValue.trim() || '',
        lastName: '',
        position: '',
        number: '',
      })
    }
    setSearchOpen(false)
    setSearchValue('')
    setOpenDialog(true)
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
    setSelectedMember(null)
    setSearchOpen(false)
  }

  const handleCreatePlayer = () => {
    setIsLoading(true)
    const payload = {
      pde_uuid: selectedMember?.uuid || null,
      position: formData.position || null,
      number: formData.number,
    }

    if (!selectedMember) {
      payload.first_name = formData.firstName
      payload.last_name = formData.lastName
    }

    createTeamPlayerInOrganization(team.uuid, payload)
      .then(() => {
        const params = { include: 'avatar' }
        if (statusFilter) {
          params.status = statusFilter
        }
        fetchTeamPlayers(team.uuid, params)
        setSearchValue('')
        setOpenDialog(false)
        setSelectedMember(null)
        setFormData({
          firstName: '',
          lastName: '',
          position: '',
          number: '',
        })
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          setErrorMessage(error.response.data.message)
        }
        if (
          error.response &&
          error.response.status === 422 &&
          error.response.data?.errors
        ) {
          const messages = Object.values(error.response.data.errors).flat()
          const validationErrors = messages.join(', ')
          setErrorMessage(validationErrors)
        }
        console.error('Error creating player:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  useEffect(() => {
    const params = { include: 'avatar' }
    if (statusFilter) {
      params.status = statusFilter
    }
    fetchTeamPlayers(team.uuid, params)
  }, [fetchTeamPlayers, team.uuid, statusFilter])

  return (
    <Hidden only={['xs', 'sm']}>
      <Paper>
        {openDialog && (
          <PlayerDialog
            open={openDialog}
            onClose={handleDialogClose}
            handleCreatePlayer={handleCreatePlayer}
            handleChange={handleChange}
            formData={formData}
            isNewPlayer={isNewPlayer}
            isLoading={isLoading}
            errorMessage={errorMessage}
          />
        )}
        {addPlayerIsOpen && !accessClubPersons && (
          <AddPlayersDialog
            open={addPlayerIsOpen}
            onClose={handleAddPlayersClose}
            handleSave={handleAddPlayersSubmit}
            loadingcircular={addPlayerIsLoading}
          />
        )}
        {isOrgPlayerDialogOpen && (
          <AddOrganizationTeamPlayersDialog
            open={isOrgPlayerDialogOpen}
            onClose={handleOrgDialogClose}
            handleAddClick={handleAddClick}
            onSearchValue={handleSearchValue}
            searchOpen={searchOpen}
            onSearchValueChange={setSearchValue}
            setSearchOpen={setSearchOpen}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        )}
        <InnrHead title={<Translate id="matches.all-players" />}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleAddPlayersOpen}
          >
            <Translate id="team-invite.add-players" />
          </Button>
        </InnrHead>
        <Box mx={2}>
          <SelectedMenu
            id="status-type"
            value={statusFilter}
            style={{ width: 180 }}
            onChange={handleStatusFilterChange}
            label={<Translate id="form.status" />}
          >
            <MenuItem value={PLAYER_STATUS_ACTIVE}>
              <Translate id="movePlayers.active" />
            </MenuItem>
            <MenuItem value={PLAYER_STATUS_DEACTIVATED}>
              <Translate id="movePlayers.inactive" />
            </MenuItem>
          </SelectedMenu>
        </Box>
        <Box px={2} pb={2} style={{ height: 464 }}>
          <DataGrid
            className={classes.dataGridTable}
            rows={rows}
            columns={columns}
            pageSize={100}
            rowHeight={60}
            headerHeight={40}
            rowsPerPageOptions={[100]}
            disableSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            sortModel={sortModel}
            onSortModelChange={(model) => handleSortModel(model)}
          />
        </Box>
      </Paper>
    </Hidden>
  )
}

TeamPlayersGrid.propTypes = {
  currentClubUuid: PropTypes.string.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      uuid: PropTypes.string.isRequired,
      relationships: PropTypes.shape({
        avatar: PropTypes.shape({
          full_url: PropTypes.string.isRequired,
        }),
      }),
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
      number: PropTypes.number,
      position: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    })
  ).isRequired,
  createTeamPlayer: PropTypes.func.isRequired,
  updatePlayer: PropTypes.func.isRequired,
  deletePlayer: PropTypes.func.isRequired,
  team: PropTypes.shape({
    id: PropTypes.number.isRequired,
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  fetchTeamPlayers: PropTypes.func.isRequired,
}

export default connect(
  ({ clubs, players, team }) => ({
    currentClubUuid: clubs.current.uuid,
    players: players.items,
    team,
  }),
  {
    createTeamPlayer: playersModule.createTeamPlayer,
    updatePlayer: playersModule.updatePlayer,
    deletePlayer: playersModule.deletePlayer,
    fetchTeamPlayers: playersModule.fetchTeamPlayers,
  }
)(TeamPlayersGrid)
